import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { getConsentChoice, saveConsent, revokeConsent } from "../lib/consent";

const LAST_UPDATED = "February 12, 2026";

const Section = ({ title, children }) => (
  <section className="mt-10">
    <h2 className="kicker text-[hsl(var(--accent))]">{title}</h2>
    <div className="mt-3 text-[hsl(var(--ink-soft))] leading-relaxed space-y-3">{children}</div>
  </section>
);

const rows = [
  {
    name: "sws_consent",
    provider: "shobhawithshubhra.com",
    category: "Strictly necessary",
    purpose: "Stores your cookie consent decision so we don't ask you again.",
    duration: "12 months",
  },
  {
    name: "sws_admin",
    provider: "shobhawithshubhra.com",
    category: "Strictly necessary",
    purpose: "Authenticates admin dashboard sessions (only set for staff).",
    duration: "Session",
  },
  {
    name: "sws_session_id (localStorage)",
    provider: "shobhawithshubhra.com",
    category: "Analytics",
    purpose:
      "Random visitor ID linking your pageviews so we can improve navigation. Contains no personal data.",
    duration: "Persistent (until cleared)",
  },
  {
    name: "sws_scroll_fired (sessionStorage)",
    provider: "shobhawithshubhra.com",
    category: "Analytics",
    purpose: "Prevents double-counting when you scroll past 75% of a page.",
    duration: "Session",
  },
];

export default function CookiePolicy() {
  const [choice, setChoice] = useState(getConsentChoice());

  useEffect(() => {
    const handler = (e) => setChoice(e.detail?.choice ?? null);
    window.addEventListener("sws:consent-changed", handler);
    return () => window.removeEventListener("sws:consent-changed", handler);
  }, []);

  const label =
    choice === "accepted"
      ? "You have accepted analytics cookies."
      : choice === "rejected"
      ? "You have rejected non-essential cookies."
      : "You have not yet made a choice.";

  return (
    <div className="pt-32">
      <section className="mx-auto max-w-3xl px-6 md:px-10 py-16">
        <div className="kicker">Cookie Policy</div>
        <h1 className="font-display text-4xl md:text-6xl leading-[1.05] mt-6">
          Cookies &amp; similar <span className="italic">technologies</span>.
        </h1>
        <p className="mt-4 text-sm text-[hsl(var(--ink-soft))]">Last updated: {LAST_UPDATED}</p>

        <div className="mt-8 border border-[hsl(var(--line))] p-5 text-sm text-[hsl(var(--ink-soft))] bg-[hsl(var(--bg-alt))]">
          <strong className="text-[hsl(var(--ink))]">Compliance disclaimer.</strong> This
          Cookie Statement is issued in line with the California CCPA / CPRA and general
          United States online-privacy best practice. It should be read together with our{" "}
          <Link to="/privacy" className="text-[hsl(var(--accent))] underline">
            Privacy Policy
          </Link>
          . Non-essential cookies are set only after you provide informed, freely-given
          consent, and you may withdraw that consent at any time with the same ease you
          granted it.
        </div>

        <Section title="What are cookies?">
          <p>
            Cookies are small text files placed on your device by the sites you visit. We also
            use similar technologies (browser <code>localStorage</code> and{" "}
            <code>sessionStorage</code>) which behave the same way for practical purposes and
            are covered by this policy. Together we refer to them as "cookies".
          </p>
        </Section>

        <Section title="Your current preference">
          <div
            data-testid="cookie-current-choice"
            className="border border-[hsl(var(--line))] p-4 bg-[hsl(var(--bg-alt))]"
          >
            <p className="text-sm text-[hsl(var(--ink))]">{label}</p>
            <div className="mt-3 flex flex-wrap gap-3">
              <button
                onClick={() => saveConsent("accepted")}
                className="text-xs tracking-[0.3em] uppercase link-underline"
                data-testid="cookie-policy-accept"
              >
                Accept all
              </button>
              <button
                onClick={() => saveConsent("rejected")}
                className="text-xs tracking-[0.3em] uppercase link-underline"
                data-testid="cookie-policy-reject"
              >
                Reject non-essential
              </button>
              <button
                onClick={() => {
                  revokeConsent();
                  setChoice(null);
                }}
                className="text-xs tracking-[0.3em] uppercase text-[hsl(var(--ink-soft))] hover:text-[hsl(var(--accent))]"
                data-testid="cookie-policy-revoke"
              >
                Withdraw &amp; re-prompt
              </button>
            </div>
          </div>
        </Section>

        <Section title="Categories we use">
          <p>
            <strong>Strictly necessary.</strong> Required for the site to work — remembering
            your consent choice, keeping staff signed in to the admin area, and preventing
            abuse of contact forms. These are essential and cannot be switched off.
          </p>
          <p>
            <strong>Analytics &amp; functional.</strong> Loaded <em>only</em> if you accept.
            They help us understand how visitors interact with the studio's work so we can
            improve navigation, portfolio ordering and page performance. We do not use these
            to build advertising profiles and we do not share the data with ad networks.
          </p>
          <p>
            <strong>Advertising / third-party trackers.</strong> We do <strong>not</strong>{" "}
            use any advertising cookies, ad-network pixels, cross-site trackers, or "sale" of
            personal information as defined by the CCPA / CPRA.
          </p>
        </Section>

        <Section title="Cookies we set">
          <div className="overflow-x-auto">
            <table className="w-full text-sm border border-[hsl(var(--line))]">
              <thead className="text-left kicker">
                <tr className="border-b border-[hsl(var(--line))] bg-[hsl(var(--bg-alt))]">
                  <th className="py-3 px-3">Name</th>
                  <th className="py-3 px-3">Category</th>
                  <th className="py-3 px-3">Purpose</th>
                  <th className="py-3 px-3">Duration</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.name} className="border-b border-[hsl(var(--line))] align-top">
                    <td className="py-3 px-3 font-display">{r.name}</td>
                    <td className="py-3 px-3">{r.category}</td>
                    <td className="py-3 px-3">{r.purpose}</td>
                    <td className="py-3 px-3 whitespace-nowrap">{r.duration}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs mt-3">
            Third-party services we call from the server (Formspree, Anthropic Claude, Google
            Gemini via Emergent, ip-api.com) do not set cookies in your browser — they process
            data server-to-server.
          </p>
        </Section>

        <Section title="California residents (CCPA / CPRA)">
          <p>
            We do <strong>not</strong> sell or share personal information as defined by the
            CCPA / CPRA. The "Reject non-essential" button is our equivalent of a Global
            Privacy Control response and disables all analytics tracking.
          </p>
        </Section>

        <Section title="Changes">
          <p>
            When we materially change this policy we bump the "Last updated" date and — where
            required by law — re-prompt you for consent.
          </p>
        </Section>

        <div className="mt-16 text-sm">
          <Link to="/privacy" className="text-[hsl(var(--accent))] underline">
            ← Read our full Privacy Policy
          </Link>
        </div>
      </section>
    </div>
  );
}
